// @ts-strict-ignore

import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {getReportFromQueryContext, getReportQueryContext, isEmptyFlowsResult} from 'common/report/flows/util';
import {AdditionalContext, isViewAsFunnelContext, QueryContext} from 'common/query-context';
import {CurrentProject} from 'common/report/model-resources/projects';
import {UpsellModalAttrs, UpsellModalProps} from 'common/widgets/upsell-modal';
import {FlowsResponse} from 'common/report/funnels/models/api-types';
import {getQueryStringParam} from 'common/contexts/react/react-router';

import {ReportManager} from '../report-manager';
import {BookmarkType} from '../queries/app-api/bookmark-client';
import {ProjectFeatureFlag, ReportName, RestrictedReport, SHOW_LAST_X_DAYS_FEATURE_FLAGS} from '../constants';
import {getReportTracker} from '../report-manager/tracking';
import {BaseQueryErrorType} from '../types';
import {ProjectDetails} from '../stores/project-details';
import {DashboardOrigin} from '../util/bookmark-overrides';
import {ClientQueryTrackingProps} from '../tracking-setup';
import {QueryContextFnParams} from '../report-manager/types';
import {FlowsBookmark} from './models/types';
import {FlowsReport} from './models/report';
import {FlowsQueryBuilderEl} from './query-builder';
import {FlowsReportHeaderEl} from './report-header';
import {FlowsQueryManager} from './query';
import {FlowsVisualizationEl} from './visualization';
import flowsUpsellImage from './images/flows-upsell.svg';
import {getDataGroupTrackingProperties, getLookupTableTrackingProperties} from './tracking';

export class FlowsReportManager extends ReportManager<FlowsBookmark, FlowsResponse, BaseQueryErrorType> {
  bookmarkType = BookmarkType.Flows;
  reportDisplayName = `Flows`;

  tracker = getReportTracker({
    appName: ReportName.Flows,
    productionPrefix: this.reportDisplayName,
  });

  isCsvExportEnabled = false;

  queryBuilderEl = FlowsQueryBuilderEl;
  reportHeaderEl = FlowsReportHeaderEl;
  visualizationEl = FlowsVisualizationEl;

  queryManager = new FlowsQueryManager({projectId: this.projectId, workspaceId: this.workspaceId});

  public getBookmarkForSaving(params: FlowsBookmark): FlowsBookmark {
    const report = FlowsReport.fromBookmarkParams(params);

    report.update({
      filters: report.filters.filter((filter) => !filter.isInherited),
    });

    return report.generateBookmark();
  }

  public hasUnsavedBookmarkParams(original: FlowsBookmark, edited: FlowsBookmark) {
    const before = FlowsReport.fromBookmarkParams(original).generateBookmark();
    const after = this.getBookmarkForSaving(edited);

    // for some reason isEqual is not working here
    return JSON.stringify(before) !== JSON.stringify(after);
  }

  public getDefaultBookmarkParams(project: CurrentProject): FlowsBookmark {
    return this.getDefaultReport(project).generateBookmark();
  }

  public isQueryable(params: FlowsBookmark) {
    const steps = params.steps;

    return Array.isArray(steps) && steps.some((step) => step.event !== null);
  }

  public hasQueryableParam(params: FlowsBookmark) {
    return params.steps.length > 0;
  }

  public isEmptyResults(results: FlowsResponse) {
    return results && isEmptyFlowsResult(results);
  }

  public getDateRange(params: FlowsBookmark): DateRange {
    return DateRange.fromBookmarkFormat(params.date_range);
  }

  public getBookmarkParamsFromQueryContext(context: QueryContext, params: QueryContextFnParams): FlowsBookmark {
    return getReportFromQueryContext(context, params.project).generateBookmark();
  }

  public getQueryContextFromBookmarkParams({
    params,
    additionalContext,
    originDashboard,
    querySamplingEnabled,
    project,
  }: {
    params: FlowsBookmark;
    additionalContext: AdditionalContext;
    originDashboard: DashboardOrigin;
    querySamplingEnabled: boolean;
    project: CurrentProject | ProjectDetails;
  }): QueryContext {
    const report = FlowsReport.fromBookmarkParams(params);
    report.querySampling = querySamplingEnabled;
    const defaultReport = this.getDefaultReport(project);
    const queryContext = getReportQueryContext(report, {defaultReport, originDashboard});
    if (queryContext && isViewAsFunnelContext(additionalContext)) {
      queryContext.viewAsFunnelContext = additionalContext.viewAsFunnelContext;
    }
    return queryContext;
  }

  public getQuerySamplingFactor(results: FlowsResponse): number {
    return results?.metadata?.min_sampling_factor ?? 1;
  }

  public getReportUpsell(project: CurrentProject): {attrs: UpsellModalAttrs; props: UpsellModalProps} {
    if (project && !project.hasAccessToReport(RestrictedReport.Flows)) {
      return {
        attrs: {
          header: `Identify top user paths and drop-off points`,
          description: `See how users navigate through your product for any workflow of your choice. Identify common user paths, see what users do right before they drop off, and find what power users do differently so you can focus on the parts of your product that matter most.`,
          'feature-gate-type': `flows`,
          'learn-more-button-url': `https://docs.mixpanel.com/docs/analysis/reports/flows`,
          open: true,
          'upgrade-url': `/pricing/?utm_source=flows_gate`,
        },
        props: {
          backgroundImage: flowsUpsellImage,
        },
      };
    }

    return null;
  }

  public getClientQueryTrackingProps({params}: {params: FlowsBookmark}): ClientQueryTrackingProps {
    const report = FlowsReport.fromBookmarkParams(params);
    return {
      ...report.getTrackingProps(),
      ...getLookupTableTrackingProperties(report),
      ...getDataGroupTrackingProperties(report),
    };
  }

  private getDefaultReport(project: CurrentProject | ProjectDetails) {
    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag) => project?.hasFeatureFlag(flag));
    const mathTypeSessions = project?.hasFeatureFlag(ProjectFeatureFlag.FlowsMathSessions)
      ? ProjectFeatureFlag.FlowsMathSessions
      : undefined;
    return FlowsReport.createDefaultReport({
      showLastXDays: showLastXDaysFlag ? showLastXDaysFlag[0] : null,
      mathTypeSessions,
      disableCustomEvents: project?.hasFeatureFlag(ProjectFeatureFlag.FlowsHideCustomEvents),
      binaryTag: getQueryStringParam(this.location, `binarytag`),
    });
  }
}
